import { useLayoutEffect, useState } from 'react';
import type { VisibleViewportBounds } from '../types/visible-viewport.types';

/**
 * Sizes an opted-in shell from the visible viewport and a coordinated handoff floor.
 * Pinch zoom retains native page sizing so this does not counteract magnification.
 * @param enabled - Whether the current consumer owns visible-viewport layout.
 * @param minimumHeight - Temporary floor while replacing a known keyboard height.
 * @returns Geometry for a fixed shell, or undefined for ordinary page layout.
 */
export const useVisibleViewport = (enabled: boolean, minimumHeight = 0) => {
  const [bounds, setBounds] = useState<VisibleViewportBounds>();

  useLayoutEffect(() => {
    if (!enabled) return;
    const viewport = window.visualViewport;
    const measure = () => {
      if (viewport && viewport.scale !== 1) {
        setBounds(undefined);
        return;
      }
      const next = {
        height: viewport?.height ?? window.innerHeight,
        offsetTop: viewport?.offsetTop ?? 0,
      };
      setBounds((previous) =>
        previous?.height === next.height &&
        previous.offsetTop === next.offsetTop
          ? previous
          : next
      );
    };
    measure();
    viewport?.addEventListener('resize', measure);
    viewport?.addEventListener('scroll', measure);
    window.addEventListener('resize', measure);
    return () => {
      viewport?.removeEventListener('resize', measure);
      viewport?.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
    };
  }, [enabled]);

  if (!enabled || !bounds) return undefined;
  return bounds.height < minimumHeight
    ? { ...bounds, height: minimumHeight }
    : bounds;
};
